import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { INDUSTRY_OPTIONS, US_STATE_OPTIONS, COUNTRY_OPTIONS } from '@/constants/formOptions';

// Type for company info fields that can be used with different form schemas
export interface CompanyInfoData {
  name: string;
  industry?: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone?: string;
  website?: string;
}

interface CompanyInfoFieldsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  selectedCountry: string;
  fieldPrefix?: string; // e.g., 'company_info' for nested forms
  showTitle?: boolean;
}

export function CompanyInfoFields({
  register,
  errors,
  selectedCountry,
  fieldPrefix = '',
  showTitle = true,
}: CompanyInfoFieldsProps) {
  // Helper to get field name with optional prefix
  const getFieldName = (field: string) => {
    return fieldPrefix ? `${fieldPrefix}.${field}` : field;
  };

  // Helper to get nested errors
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getError = (field: string): any => {
    if (fieldPrefix) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const prefixErrors = errors[fieldPrefix] as any;
      return prefixErrors?.[field];
    }
    return errors[field];
  };

  return (
    <div className={showTitle ? 'border-t pt-6' : ''}>
      {showTitle && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name *
          </label>
          <input
            {...register(getFieldName('name'))}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {getError('name') && (
            <p className="mt-1 text-sm text-red-600">{getError('name')?.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Industry
          </label>
          <select
            {...register(getFieldName('industry'))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {INDUSTRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 1 *
          </label>
          <input
            {...register(getFieldName('address_line1'))}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {getError('address_line1') && (
            <p className="mt-1 text-sm text-red-600">
              {getError('address_line1')?.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 2
          </label>
          <input
            {...register(getFieldName('address_line2'))}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
          <select
            {...register(getFieldName('country'))}
            defaultValue="US"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {COUNTRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {getError('country') && (
            <p className="mt-1 text-sm text-red-600">{getError('country')?.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
            <input
              {...register(getFieldName('city'))}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {getError('city') && (
              <p className="mt-1 text-sm text-red-600">{getError('city')?.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {selectedCountry === 'US' ? 'State *' : 'State/Province *'}
            </label>
            {selectedCountry === 'US' ? (
              <select
                {...register(getFieldName('state'))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {US_STATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                {...register(getFieldName('state'))}
                type="text"
                placeholder="State/Province"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
            {getError('state') && (
              <p className="mt-1 text-sm text-red-600">{getError('state')?.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postal Code *
            </label>
            <input
              {...register(getFieldName('postal_code'))}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {getError('postal_code') && (
              <p className="mt-1 text-sm text-red-600">
                {getError('postal_code')?.message}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              {...register(getFieldName('phone'))}
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <input
              {...register(getFieldName('website'))}
              type="url"
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {getError('website') && (
              <p className="mt-1 text-sm text-red-600">
                {getError('website')?.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
